/**
 * @fileoverview System-web hook that resolves the effective theme applying the
 * plan-defined precedence chain: user override → app default.
 * @module system-web/hooks/useEffectiveTheme
 *
 * Responsibilities:
 * - Compose useUserThemePreference.
 * - Derive the effective theme id (user override → 'default' fallback).
 * - Expose the setter for the user override.
 *
 * Dependencies:
 * - useUserThemePreference (reads/writes system_users.preferences.themeId)
 *
 * Architecture:
 * - Pure composition; no direct Dexie or API access here.
 * - Consumed by ThemeBootstrap (App.tsx) to feed the effectiveThemeId into ThemeProvider.
 * - Also consumed by AdminLayout profile area to render the active theme and allow changes.
 *
 * Notes:
 * - system-web has no tenant-level theme default concept; the precedence chain is
 *   user override → app default ('default').
 * - The 'app-default' string is the canonical first-entry id in the THEMES catalog.
 */

import { useUserThemePreference } from './useUserThemePreference';

/**
 * @contract SystemEffectiveThemePreference
 *
 * Purpose:
 * - Full resolved theme state for system-web.
 *
 * Fields:
 * - effectiveThemeId: Resolved theme id (user → 'default').
 * - userThemeId: Raw user override or null.
 * - userId: System user id from Dexie.
 * - source: Which layer determined the effective theme.
 * - setUserThemeId: Write user override (pass null to clear).
 * - loading: True while the Dexie query is resolving.
 * - error: Non-null when the subscription fails.
 * - resolved: True only when the authenticated user's local record is available.
 *
 * Rules:
 * - effectiveThemeId is always a non-null string; never undefined.
 *
 * Sync:
 * - The setter is Dexie-first and triggers background sync via wakeLocalWrite.
 */
export interface SystemEffectiveThemePreference {
  effectiveThemeId: string;
  userThemeId: string | null;
  userId: string | null;
  source: 'user' | 'app-default';
  setUserThemeId: (themeId: string | null) => Promise<void>;
  loading: boolean;
  error: string | null;
  resolved: boolean;
}

/**
 * @hook useEffectiveTheme
 *
 * Purpose:
 * - Resolve the final theme id for system-web (user override → app default)
 *   and expose the write path for the user override.
 *
 * Returns:
 * - SystemEffectiveThemePreference
 *
 * Side Effects:
 * - Subscribes to system_users (via useUserThemePreference).
 *
 * Offline Behavior:
 * - Reads from local Dexie; available offline.
 * - Writes are local-first; synced to server when connectivity resumes.
 */
export function useEffectiveTheme(): SystemEffectiveThemePreference {
  const { userThemeId, userId, setUserThemeId, loading, error, resolved } = useUserThemePreference();

  const effectiveThemeId = userThemeId ?? 'default';
  const source: SystemEffectiveThemePreference['source'] = userThemeId ? 'user' : 'app-default';

  return { effectiveThemeId, userThemeId, userId, source, setUserThemeId, loading, error, resolved };
}
